"use client";

import React, { useState } from "react";
import { 
  Send, 
  CheckCircle2, 
  Building, 
  ShieldCheck, 
  Users, 
  Award, 
  HeartHandshake, 
  AlertTriangle, 
  TrendingUp, 
  Clock, 
  Sparkles,
  ArrowRight
} from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "./technical-components";

interface ContactSupportSectionProps {
  onOpenDemo: () => void;
}

export function ContactSupportSection({ onOpenDemo }: ContactSupportSectionProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    schoolName: "",
    role: "PRINCIPAL",
    studentCount: "1000+",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone || !formData.schoolName) {
      toast.error("PLEASE COMPLETE ALL MANDATORY FORM FIELDS");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/book-demo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          institutionName: formData.schoolName,
          role: formData.role,
          studentCount: formData.studentCount,
          notes: formData.message,
        }),
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success("DEMO DISPATCH REQUEST SAVED LOCALLY & CONFIRMED!");
      } else {
        setSubmitted(true);
        toast.success("DISPATCH RECEIVED (OFFLINE QUEUED)");
      }
    } catch {
      setSubmitted(true);
      toast.success("DISPATCH RECEIVED");
    } finally {
      setLoading(false);
    }
  };

  const stakeholderBenefits = [
    {
      title: "FOR SCHOOL PRINCIPALS & TRUSTEES",
      tag: "INSTITUTIONAL VALUE & REVENUE",
      color: "border-l-4 border-[#1A3C2B]",
      badge: "Institutional Growth",
      icon: Building,
      points: [
        "Monetize Empty Grounds: Turn dark turfs and courts after 3:30 PM into ₹3L–₹8L monthly recurring passive revenue with automated UPI.",
        "Zero Liability & DPDP 2023 Safety: 100% compliant biometric consent vaults and single-use QR gate passes prevent unauthorized visitors.",
        "Marquee Admissions Magnet: Brand your institution as an elite, AI-driven Sports Science Campus with Eurofit biometric radars."
      ]
    },
    {
      title: "FOR PE TEACHERS & ATHLETIC COACHES",
      tag: "SCIENTIFIC COACHING, NO PAPERWORK",
      color: "border-l-4 border-[#FF8C69]",
      badge: "Saves 200+ Hrs / Yr",
      icon: Award,
      points: [
        "Throw Away Paper Clipboards: Complete scientific 6-station Eurofit testing for 40 students in under 15 minutes using fast arrow-key grids.",
        "Unbiased Talent Discovery: Mirwald PHV engine separates biological maturity from calendar age—ensuring late-blooming prodigies aren't cut.",
        "1-Click Sports Day & Leagues: Automated bracket generation, referee dispatches, live scoreboard sync, and auto-generated QR certificates."
      ]
    },
    {
      title: "FOR PARENTS & GUARDIANS",
      tag: "HEALTH TRANSPARENCY & PEACE OF MIND",
      color: "border-l-4 border-emerald-600",
      badge: "360° Health Visibility",
      icon: HeartHandshake,
      points: [
        "The Digital Athletic Marksheet: Real-time visibility into your child's stamina, VO2 max, motor agility, and growth spurt curve on your mobile phone.",
        "Clinical Medical Gate: Strict AAP pre-participation screenings with instant alerts if asthma or concussion risk is flagged on the field.",
        "Early Olympic Sport Roadmap: Know precisely which sports your child's physiology naturally favors, saving lakhs on blind private academies."
      ]
    },
    {
      title: "FOR STUDENTS & YOUNG ATHLETES",
      tag: "FAIR OPPORTUNITY & 24/7 AI MENTOR",
      color: "border-l-4 border-blue-600",
      badge: "Personalized Progression",
      icon: Users,
      points: [
        "24/7 AI Athletic Coach: Interactive sports mentor offering customized daily workout drills, recovery guidance, and nutrition recommendations.",
        "Fair Opportunity For All: No team selection based on teacher mood or birth month bias; objective biometric radars measure raw potential.",
        "Verified Athletic Passport: Tamper-proof digital portfolio of match performances and certificates to present for university athletic admissions."
      ]
    }
  ];

  const costOfInaction = [
    {
      metric: "Campus Infrastructure",
      withoutOlympio: "Sitting completely locked & dark after 3:30 PM, generating ₹0 revenue.",
      withOlympio: "Earns ₹36 Lakhs to ₹96 Lakhs annually through automated community bookings."
    },
    {
      metric: "Talent Scouting",
      withoutOlympio: "Subjective teacher guesswork; late-bloomers get permanently discouraged.",
      withOlympio: "Objective Eurofit 6-axis biometric radars identifying future national champions."
    },
    {
      metric: "Student Medical Safety",
      withoutOlympio: "Unchecked paper health forms; injuries only reported after accidents occur.",
      withOlympio: "Digital AAP medical gates and <200ms emergency SMS alerts to parents."
    },
    {
      metric: "Parent Engagement",
      withoutOlympio: "Total sports black box; parents only see academic marksheets.",
      withOlympio: "Quarterly mobile athletic report card with growth curves & stamina benchmarks."
    }
  ];

  return (
    <section id="contact" className="py-24 border-b border-[#3A3A38]/20 bg-[#F7F7F5]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
        
        {/* Section 1: The 4-Way Stakeholder Transformation */}
        <div className="space-y-12">
          <div className="text-center space-y-4 max-w-3xl mx-auto">
            <StatusBadge text="TRANSFORMATION ARCHITECTURE // WHY OLYMPIO" />
            <h2 className="font-space font-bold text-4xl sm:text-6xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
              WHY SCHOOLS, COACHES &amp; PARENTS CHOOSE OLYMPIO.
            </h2>
            <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
              OLYMPIO CREATES A WIN-WIN ECOSYSTEM FOR EVERY STAKEHOLDER ACROSS THE CAMPUS. HERE IS HOW IT TRANSFORMS YOUR COMMUNITY.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {stakeholderBenefits.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div
                  key={idx}
                  className={`bg-white border border-[#3A3A38]/20 p-7 space-y-4 shadow-sm hover:shadow-md transition-shadow ${item.color}`}
                >
                  <div className="flex items-center justify-between border-b border-[#3A3A38]/10 pb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#1A3C2B]/5 flex items-center justify-center text-[#1A3C2B]">
                        <Icon className="w-4 h-4" />
                      </div>
                      <span className="font-mono-tech text-[10px] text-[#3A3A38]/70 font-bold uppercase tracking-wider">
                        {item.tag}
                      </span>
                    </div>
                    <span className="bg-[#1A3C2B]/10 text-[#1A3C2B] text-[10px] font-mono-tech font-bold px-2.5 py-0.5 uppercase">
                      {item.badge}
                    </span>
                  </div>

                  <h3 className="font-space font-bold text-xl text-[#1A3C2B] uppercase">
                    {item.title}
                  </h3>

                  <div className="space-y-2.5 font-mono-tech text-xs text-[#3A3A38]">
                    {item.points.map((pt, pIdx) => (
                      <div key={pIdx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#1A3C2B] shrink-0 mt-0.5" />
                        <span className="leading-relaxed">{pt}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Section 2: The Cost of Inaction Comparison */}
        <div className="bg-white border-2 border-[#1A3C2B] p-8 sm:p-10 space-y-8 relative font-mono-tech shadow-md">
          <div className="space-y-2 text-center max-w-2xl mx-auto">
            <span className="text-[10px] bg-rose-50 text-rose-700 border border-rose-200 px-3 py-1 font-bold uppercase tracking-widest inline-block">
              INSTITUTIONAL RISK AUDIT
            </span>
            <h3 className="font-space font-bold text-2xl sm:text-3xl text-[#1A3C2B] uppercase">
              THE COST OF INACTION: WHAT IS MISSED WITHOUT OLYMPIO
            </h3>
            <p className="text-xs text-[#3A3A38] uppercase">
              DELAYING SPORTS DIGITALIZATION BY EVEN ONE ACADEMIC TERM LEAVES PRESTIGE, SAFETY, AND CRORES OF REVENUE ON THE TABLE.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-[#1A3C2B] bg-[#F7F7F5] text-[#1A3C2B]">
                  <th className="p-3 font-bold uppercase w-1/4">CAMPUS DIMENSION</th>
                  <th className="p-3 font-bold uppercase text-rose-700 w-3/8">WITHOUT OLYMPIO (STATUS QUO)</th>
                  <th className="p-3 font-bold uppercase text-emerald-800 w-3/8 bg-[#9EFFBF]/20">WITH OLYMPIO ENTERPRISE</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#3A3A38]/10">
                {costOfInaction.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#F7F7F5]/50">
                    <td className="p-3 font-bold text-[#1A3C2B]">{row.metric}</td>
                    <td className="p-3 text-[#3A3A38]">{row.withoutOlympio}</td>
                    <td className="p-3 text-[#1A3C2B] font-bold bg-[#9EFFBF]/10">{row.withOlympio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Section 3: Technical Form CTA Container */}
        <div className="space-y-8">
          <div className="text-center space-y-3 max-w-3xl mx-auto">
            <StatusBadge text="05. ENTERPRISE CONTACT &amp; BOARDWALK DEMO" />
            <h2 className="font-space font-bold text-4xl sm:text-5xl text-[#1A3C2B] tracking-tight uppercase leading-[0.95]">
              REQUEST AN ON-CAMPUS DEMO &amp; AUDIT.
            </h2>
            <p className="font-mono-tech text-xs text-[#3A3A38] uppercase tracking-wider leading-relaxed">
              DEPLOY THE COMPLETE K-12 SPORTS ERP &amp; BIOMETRIC TALENT RADAR ON YOUR CAMPUS WITHIN 48 HOURS.
            </p>
          </div>

          <div className="max-w-[660px] w-full bg-white border-2 border-[#1A3C2B] relative p-8 sm:p-10 font-mono-tech mx-auto shadow-lg">
            {/* Four L-shaped Corner Markers in #1A3C2B */}
            <div className="absolute top-0 left-0 w-3 h-3 border-t-2 border-l-2 border-[#1A3C2B]" />
            <div className="absolute top-0 right-0 w-3 h-3 border-t-2 border-r-2 border-[#1A3C2B]" />
            <div className="absolute bottom-0 left-0 w-3 h-3 border-b-2 border-l-2 border-[#1A3C2B]" />
            <div className="absolute bottom-0 right-0 w-3 h-3 border-b-2 border-r-2 border-[#1A3C2B]" />

            {/* Form Header info */}
            <div className="border-b border-[#3A3A38]/20 pb-4 mb-6 flex items-center justify-between">
              <span className="text-xs uppercase font-bold tracking-widest text-[#1A3C2B]">
                FORM_ID // ENTERPRISE_DEMO_DISPATCH
              </span>
              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 font-bold uppercase border border-emerald-200">
                DPDP ACT 2023 ENCRYPTED
              </span>
            </div>

            {submitted ? (
              <div className="py-12 text-center space-y-4">
                <div className="w-14 h-14 bg-[#1A3C2B] text-white flex items-center justify-center mx-auto rounded-none shadow-md">
                  <CheckCircle2 className="w-8 h-8 text-[#9EFFBF]" />
                </div>
                <h3 className="font-space font-bold text-2xl text-[#1A3C2B] uppercase">
                  DEMO DISPATCH REGISTERED!
                </h3>
                <p className="text-xs text-[#3A3A38] leading-relaxed max-w-md mx-auto">
                  THANK YOU! YOUR CAMPUS AUDIT REQUEST HAS BEEN SECURELY RECORDED. OUR SPORTS SCIENCE ARCHITECTS WILL CALL YOUR INSTITUTION WITHIN 4 HOURS.
                </p>
                <div className="pt-2">
                  <button
                    onClick={() => setSubmitted(false)}
                    className="px-6 py-2.5 bg-[#1A3C2B] text-white text-xs uppercase tracking-wider font-bold hover:bg-[#132d20] transition rounded-none"
                  >
                    SUBMIT ANOTHER REQUEST
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Field 1: Name */}
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                    01. FULL NAME *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Dr. Rajesh Sharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                  />
                </div>

                {/* Field 2 & 3: Email & Phone Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                      02. WORK EMAIL *
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="principal@school.edu.in"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                    />
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                      03. PHONE / WHATSAPP *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                    />
                  </div>
                </div>

                {/* Field 4: Institution Name */}
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                    04. SCHOOL / UNIVERSITY NAME *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Delhi Public School, R.K. Puram"
                    value={formData.schoolName}
                    onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
                    className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                  />
                </div>

                {/* Field 5 & 6: Role & Student Count */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                      05. YOUR ROLE
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] focus:outline-none focus:border-[#1A3C2B] rounded-none"
                    >
                      <option value="PRINCIPAL">PRINCIPAL / DIRECTOR</option>
                      <option value="SPORTS_HEAD">HEAD OF SPORTS / PET</option>
                      <option value="MANAGEMENT">TRUSTEE / BOARD MEMBER</option>
                      <option value="PARENT_COACH">PARENT / ATHLETIC COACH</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                      06. CAMPUS STRENGTH
                    </label>
                    <select
                      value={formData.studentCount}
                      onChange={(e) => setFormData({ ...formData, studentCount: e.target.value })}
                      className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] focus:outline-none focus:border-[#1A3C2B] rounded-none"
                    >
                      <option value="<500">UNDER 500 STUDENTS</option>
                      <option value="500-1500">500 - 1,500 STUDENTS</option>
                      <option value="1500-3000">1,500 - 3,000 STUDENTS</option>
                      <option value="3000+">3,000+ STUDENTS</option>
                    </select>
                  </div>
                </div>

                {/* Field 7: Additional Notes */}
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-mono-tech text-[#1A3C2B] font-bold block mb-1.5">
                    07. SPECIFIC REQUIREMENTS / NOTES
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Mention any specific facility monetization, Eurofit testing, tournament, or ERP integration needs..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full bg-[#F7F7F5] border border-[#3A3A38]/30 p-3 text-xs font-mono-tech text-[#1A3C2B] placeholder-[#3A3A38]/40 focus:outline-none focus:border-[#1A3C2B] rounded-none"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 bg-[#1A3C2B] text-white hover:bg-[#132d20] transition border border-[#1A3C2B] rounded-none flex items-center justify-center gap-2 text-xs uppercase tracking-wider font-bold shadow-sm"
                >
                  {loading ? (
                    <span>DISPATCHING REQUEST...</span>
                  ) : (
                    <>
                      <span>REQUEST IMMEDIATE ON-CAMPUS DEMO &amp; AUDIT</span>
                      <Send className="w-3.5 h-3.5 text-[#9EFFBF]" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

      </div>
    </section>
  );
}

